import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { FileText, Copy, Loader2, BookOpen } from 'lucide-react'
import { toast } from 'sonner'

interface Template {
  id: string
  name: string
  description: string | null
  vertical: string
  merge_fields: Array<{ name: string; label: string; type: string; required: boolean }>
  content_preview: string
}

interface UserTemplate {
  id: string
  name: string
  description: string | null
  vertical: string
  mergeFields: string | null
  isPublic: boolean
  createdAt: string
  updatedAt: string
}

export function TemplatesPage() {
  const [tab, setTab] = useState<'library' | 'mine'>('library')
  const [libraryTemplates, setLibraryTemplates] = useState<Template[]>([])
  const [userTemplates, setUserTemplates] = useState<UserTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [copying, setCopying] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      fetch('/api/v1/templates/library').then(r => r.json() as Promise<{ templates: Template[] }>).then((d) => d.templates),
      fetch('/api/v1/templates').then(r => r.ok ? r.json() as Promise<{ templates: UserTemplate[] }> : Promise.resolve({ templates: [] as UserTemplate[] })).then((d) => d.templates),
    ]).then(([library, mine]) => {
      setLibraryTemplates(library)
      setUserTemplates(mine)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const handleCopy = async (templateId: string) => {
    setCopying(templateId)
    try {
      const res = await fetch(`/api/v1/templates/${templateId}/copy`, { method: 'POST' })
      if (res.ok) {
          // Refresh user templates
        const mine = await fetch('/api/v1/templates').then(r => r.json()) as { templates: UserTemplate[] }
        setUserTemplates(mine.templates)
        setTab('mine')
        toast.success('Template copied to your collection')
      } else {
        toast.error('Failed to copy template')
      }
    } catch {
      toast.error('Failed to copy template')
    } finally {
      setCopying(null)
    }
  }

  // Strip HTML tags for safe display
  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

  return (
    <div className="container max-w-5xl py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Templates</h1>
        <p className="text-muted-foreground">
          Browse the template library or manage your own templates.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'library' | 'mine')}>
        <TabsList className="mb-6">
          <TabsTrigger value="library">
            <BookOpen className="mr-2 h-4 w-4" />
            Template Library
          </TabsTrigger>
          <TabsTrigger value="mine">
            <FileText className="mr-2 h-4 w-4" />
            My Templates ({userTemplates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library">
          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-48" />
              ))}
            </div>
          ) : libraryTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No templates available yet.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {libraryTemplates.map((template) => (
                <Card key={template.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <FileText className="h-4 w-4 text-primary" />
                      {template.name}
                    </CardTitle>
                    {template.description && (
                      <CardDescription>{template.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {stripHtml(template.content_preview)}
                    </p>
                    {template.merge_fields.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {template.merge_fields.map((f) => (
                          <Badge key={f.name} variant="outline" className="text-xs">
                            {`{{${f.name}}}`}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopy(template.id)}
                      disabled={copying === template.id}
                    >
                      {copying === template.id ? (
                        <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      ) : (
                        <Copy className="mr-2 h-3 w-3" />
                      )}
                      Copy to My Templates
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mine">
          {loading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-40" />
              ))}
            </div>
          ) : userTemplates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No templates yet.</p>
              <p className="text-sm text-muted-foreground">
                Copy a template from the library to get started.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {userTemplates.map((template) => {
                const mergeFields = template.mergeFields
                  ? (JSON.parse(template.mergeFields) as Array<{ name: string }>)
                  : []
                return (
                  <Card key={template.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText className="h-4 w-4 text-primary" />
                        {template.name}
                      </CardTitle>
                      {template.description && (
                        <CardDescription>{template.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      {mergeFields.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {mergeFields.map((f) => (
                            <Badge key={f.name} variant="outline" className="text-xs">
                              {`{{${f.name}}}`}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Updated {new Date(template.updatedAt).toLocaleDateString('en-AU')}
                      </p>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
